import { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { InstHeader } from "./InstHeader";
import { Footer } from "@/components/Footer";

export const InstLayout = ({ children }: { children: ReactNode }) => {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <InstHeader />
      <main className="flex-1 pb-20">{children}</main>
      <Footer />
    </div>
  );
};

export const Eyebrow = ({ children }: { children: ReactNode }) => (
  <span className="inline-block text-xs font-semibold uppercase tracking-[0.18em] text-primary mb-4">
    {children}
  </span>
);

export const SectionHeading = ({
  eyebrow,
  title,
  description,
  as: Tag = "h2",
}: {
  eyebrow?: string;
  title: ReactNode;
  description?: ReactNode;
  as?: "h1" | "h2" | "h3";
}) => (
  <header className="max-w-3xl mb-12">
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    <Tag className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-foreground mb-5">
      {title}
    </Tag>
    {description && (
      <p className="text-lg text-muted-foreground leading-relaxed">{description}</p>
    )}
  </header>
);

export const Container = ({ children, className = "" }: { children: ReactNode; className?: string }) => (
  <div className={`container mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

export const SignalCard = ({
  title,
  description,
  icon: Icon,
}: {
  title: string;
  description: string;
  icon: LucideIcon;
}) => (
  <article className="rounded-2xl border border-border bg-card p-7 hover:border-primary/40 transition-colors">
    <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
      <Icon className="w-5 h-5 text-primary" aria-hidden={true} />
    </div>
    <h3 className="font-display text-xl font-semibold text-foreground mb-2">{title}</h3>
    <p className="text-muted-foreground leading-relaxed">{description}</p>
  </article>
);

export const TrustBar = ({ note }: { note: string }) => (
  <aside className="rounded-2xl border border-border bg-muted/30 p-6 md:p-8 my-12" aria-label="Human oversight note">
    <p className="text-sm uppercase tracking-wider text-primary font-semibold mb-2">Designed for human oversight</p>
    <p className="text-foreground leading-relaxed">{note}</p>
  </aside>
);
